import React, { useCallback, useEffect, useState } from "react";
import {
  TableContainer,
  Paper,
  Button,
  Switch,
  Card,
  Box,
  Stack,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ProductCreationModal from "./create";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";
import productApi from "../../../services/product";
import CustomDatagrid from "../../../components/datagrid";

const ProductList = () => {
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [fileId, setFileId] = useState();
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [status, setStatus] = useState({
    product_uuid: "",
    status: "",
  });
  const menuItems = useSelector((state) => state.menuReducer.items);
  const createMenuItem = menuItems
    ?.find((item) => item.name.toLowerCase() === "products")
    ?.submenus?.find((item) => item.name.toLowerCase() === "create");
  const isDisabled = createMenuItem?.access_type === "read_write";

  const getAllProduct = useCallback(async () => {
    try {
      dispatch(showLoader());
  
      const res = await productApi.getAllProduct(
        paginationModel.page +1, 
        paginationModel.pageSize
      );
  
      if (res?.data?.status) {
        setProducts(res.data.data || []);
        setRowCount(res?.data?.pagination?.totalItems || 0);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      dispatch(hideLoader());
    }
  }, [paginationModel, dispatch]);

  useEffect(() => {
    getAllProduct(paginationModel.page,paginationModel.pageSize );
  }, [getAllProduct, paginationModel]);

  const handleEditProduct = (productId) => {
    dispatch(showLoader());
    const onSuccess = (res) => {
      if (res?.data?.status === true) {
        dispatch(hideLoader());
        setSelectedProduct(res?.data?.data);
        setMode("edit");
        setModalOpen(true);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      console.error(err);
    };
    productApi?.getAllProductById(productId).then(onSuccess, onFailure);
  };

  const handleStatusChange = (uuid) => {
    setProducts((prevProducts) => {
      const updatedProducts = prevProducts.map((product) => {
        if (product && product.uuid === uuid) {
          const updatedProduct = { ...product, status: !product.status };
          setStatus({ product_uuid: uuid, status: updatedProduct.status });
          return updatedProduct;
        }
        return product;
      });

      return updatedProducts;
    });
  };
  useEffect(() => {
    if (status.product_uuid && status.status !== "") {
      handleStatus(status);
    }
  }, [status]);

  const handleStatus = (status) => {
    const productToUpdate = status?.product_uuid;
    const updatedStatus = status?.status;

    if (!productToUpdate || updatedStatus === undefined) {
      console.error("Invalid status or product_uuid", status);
      return;
    }

    dispatch(showLoader());
    productApi
      .updateProduct(productToUpdate, { status: updatedStatus })
      .then((res) => {
        dispatch(hideLoader());
        if (res?.data?.status === true) {
          setProducts((prevProducts) => {
            const updatedProducts = prevProducts.map((product) =>
              product.uuid === productToUpdate
                ? { ...product, status: updatedStatus }
                : product
            );
            return updatedProducts;
          });
        } else {
          console.error(res?.data?.msg || "Failed to update status");
        }
      })
      .catch((err) => {
        dispatch(hideLoader());
        console.error("API call failed:", err);
      });
  };

  const onClose = () => {
    setModalOpen(false);
    setFileId();
  };

  const productListRows = products.map((data, index) => ({
    sno_id: index + 1,
    id: index + 1,
    uuid: data.uuid,
    product_id: data.product_id,
    name: data.name,
    category: data.productCategory?.name,
    entity: data.entityDetails?.entity_name,
    hsn_code: data.hsn_code,
    product_url: data.productUrl,
    product_login_url: data.product_login_url,
    status: data?.status,
  }));

  const columns = [
    {
      field: "sno_id",
      headerName: "S.No",
      width: 100,
      headerAlign: "left",
      align: "center",
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
      sortable: false,
    },
    {
      field: "product_id",
      headerName: "Product ID",
      width: 150,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "name",
      headerName: "Product Name",
      width: 250,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "category",
      headerName: "Category",
      width: 200,
      headerAlign: "left",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
      disableColumnMenu: true,
    },
    {
      field: "entity",
      headerName: "Entity",
      width: 200,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "hsn_code",
      headerName: "HSN Code",
      width: 150,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "product_url",
      headerName: "Product URL",
      width: 150,
      headerAlign: "left",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
      disableColumnMenu: true,
    },
    {
      field: "product_login_url",
      headerName: "Product Login URL",
      width: 150,
      headerAlign: "left",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
      disableColumnMenu: true,
    },
    ...(!isDisabled
      ? [
          {
            field: "status",
            headerName: "Status",
            width: 150,
            disableColumnMenu: true,
            sortable: false,
            headerAlign: "left",
            align: "center",
            renderCell: (params) => {
              return (
                <Chip
                  label={params?.row?.status ? "Active" : "In-Active"}
                  color={params?.row?.status ? "success" : "error"}
                  variant="outlined"
                />
              );
            },
          },
        ]
      : []),
    ...(isDisabled
      ? [
          {
            field: "status",
            headerName: "Status",
            width: 150,
            sortable: false,
            disableColumnMenu: true,
            headerAlign: "left",
            align: "center",
            renderCell: (params) => {
              return (
                <Switch
                  checked={params.row.status}
                  onChange={() => handleStatusChange(params.row.uuid)}
                  color="primary"
                />
              );
            },
          },
          {
            field: "action",
            headerName: "Action",
            disableColumnMenu: true,
            sortable: false,
            width: 150,
            headerAlign: "left",
            align: "center",
            renderCell: (params) => (
              <div style={{ textAlign: "left", width: "100%" }}>
                <EditIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleEditProduct(params.row.uuid)}
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <Box style={{ padding: "20px" }}>
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        {isDisabled && (
          <Button
            variant="contained"
            onClick={() => {
              setMode("create");
              setModalOpen(true);
            }}
          >
            Create Product
          </Button>
        )}
      </Stack>
      <Card style={{ marginTop: "20px" }} elevation={3}>
        <TableContainer component={Paper}>
          <CustomDatagrid
            rows={productListRows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={(params)=>setPaginationModel(params)}
            rowCount={rowCount}
            disableSelectionOnClick={true}
            checkboxSelection={false}
            pagination
            pageSizeOptions={[5, 10, 20]}
          />
        </TableContainer>
      </Card>

      <ProductCreationModal
        open={isModalOpen}
        onClose={onClose}
        mode={mode}
        product={selectedProduct}
        getAllProduct={getAllProduct}
        fileId={fileId}
        setFileId={setFileId}
      />
    </Box>
  );
};

export default ProductList;
